// ── 케이스 통합 뷰 (읽기 정본) ──────────────────────────────────────────────
// 기획서: docs/기획서_케이스중심통일_2026-08.md 2-3절 / 1단계(읽기 전환 + 섀도 비교)
//
// 목적: 같은 기공물 1건이 (①웹 의뢰서 / ②agent_auto 자동등록 주문 / ③parsedCases 수집분)
//       3벌로 존재하면서, 화면마다 중복 제거 규칙이 따로 구현돼 있던 것을 **1벌**로 모은다.
//       대체 대상 3곳:
//         - CreateTransactionStatement.js:1109-1194 (agentDeduped2) — 명세서 청구(돈)
//         - RevenueQuickView.js:78-98               — 예상 매출
//         - ProductionBoard.js:217-256              — 제작 현황 카드
//
// ★ 1단계 원칙: 화면 출력은 바꾸지 않는다. 이 파일의 병합 결과는 섀도 비교(diff 로깅)에만 쓴다.
//   따라서 **현행 명세서 경로(CreateTransactionStatement)의 규칙을 글자 그대로** 옮겼다.
//   "더 옳아 보이는" 보정은 넣지 않는다(넣으면 diff가 나서 검증 자체가 무의미해진다).
//   현행과 다르게 갈 수 있는 지점은 전부 옵션으로 빼고 기본값을 현행에 맞췄다:
//     - nfcNormalize (기본 false) : 환자·치과명 NFC 정규화 (LEDGER 나중에볼것 #22)
//     - remakeAgainstWeb (기본 false) : 웹 우선 병합에서 리메이크 수집분 보호
//       (RevenueQuickView.js:95는 보호하고, 명세서 경로는 보호하지 않는 기존 불일치)
//
// 병합 키 = 에이전트 dedupId v2와 동일 축:
//   치과 | 환자명(정규화·2글자 포함관계 허용) | 치식 집합 | 작업월(±7일 예외) | 재료 집합(부분집합)
//   재료 정규화·부분집합 판정은 공통 정본 utils/shared/material-alias.js 사용(에이전트와 같은 사전).

import { displayPatientName } from './patientName';
import { getTimestamp } from './statementItems';
import { normMaterialToken, isMaterialSubset } from './shared/material-alias';

export const ORIGIN = { WEB: 'web', COLLECTED: 'collected', BOTH: 'both' };
export const ORIGIN_LABEL = { web: '웹 의뢰서', collected: '자동 수집', both: '웹+수집' };

// ── 정규화 키 ───────────────────────────────────────────────────────────────
// 환자명 — 공백·숫자·특수문자 제거 ("3한수영" → "한수영"). CreateTransactionStatement.js:1118 동일
export const normPatientKey = (n, { nfcNormalize = false } = {}) => {
    const s = String(n == null ? '' : n);
    return (nfcNormalize ? s.normalize('NFC') : s).replace(/[\s\d\-_.()[\]]/g, '').toLowerCase();
};
// 두 이름이 같은 환자인가 — 완전일치 또는 2글자 이상 포함관계 (CreateTransactionStatement.js:1120-1124 동일)
export const samePatientName = (a, b) => {
    if (!a || !b) return false;
    if (a === b) return true;
    return a.length >= 2 && b.length >= 2 && (a.includes(b) || b.includes(a));
};
// 치과명 — 법인격 표기 제거 (CreateTransactionStatement.js:928 normBiz 동일)
export const normClinicKey = (n, { nfcNormalize = false } = {}) => {
    const s = String(n == null ? '' : n);
    return (nfcNormalize ? s.normalize('NFC') : s)
        .replace(/\s|\(.*?\)/g, '').replace(/치과의원|치과병원|치과$/g, '').toLowerCase();
};

// 치식 키 — items + prosthesisGroups + bridgeGroups (현행 teethOf와 동일. pontic/implantGroups는 현행도 미포함)
export const teethKeyOf = (o) => {
    const teeth = [];
    (o.items || []).forEach(it => { if (it.toothNumber) teeth.push(String(it.toothNumber)); });
    (o.prosthesisGroups || []).forEach(g => (g.teeth || []).forEach(t => teeth.push(String(t))));
    (o.bridgeGroups || []).forEach(g => (g.teeth || []).forEach(t => teeth.push(String(t))));
    return [...new Set(teeth)].sort().join(',');
};

// 재료 집합 — 공통 정본 사전으로 정규화한 토큰 집합 (CreateTransactionStatement.js:1158 동일)
export const materialSetOf = (o) => [...new Set((o.items || []).map(it => normMaterialToken(it.material)).filter(Boolean))];

// 작업월 — createdAt 기준 (현행 monthOf2와 동일. dateBasis가 dueDate여도 월 판정은 createdAt)
export const monthKeyOf = (o) => {
    const t = getTimestamp(o.createdAt);
    if (!t) return '';
    const d = new Date(t);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// 내용 있는 의뢰서인가 — 빈 의뢰서(테스트 제출 등) 제외 (CreateTransactionStatement.js:1134-1137 동일)
export const hasContent = (o) => (
    (o.items || []).length > 0 || (o.prosthesisGroups || []).length > 0 ||
    (o.bridgeGroups || []).length > 0 || (o.ponticGroups || []).length > 0 ||
    (o.implantGroups || []).length > 0 || (o.teethDetails || []).length > 0
);

// ── parsedCase → 주문 형태 변환 ─────────────────────────────────────────────
// 자동등록(agent_auto) 주문을 만들지 않고도 기존 항목 생성기(buildItemsFromOrders)를 그대로 쓰기 위한 어댑터.
// 필드 구성은 CreateTransactionStatement.js:902-913(buildItemsFromParsed) / :956-973(autoRegisterParsedForClinic)와
// 1:1로 맞춘다 — 금액이 1원도 달라지면 안 되므로 임의 보정 금지.
export const caseItemsOf = (r) => {
    const indication = (r.indications || [])[0] || '';
    return (r.teethDetails && r.teethDetails.length)
        ? r.teethDetails.map(d => ({
            toothNumber: d.tooth, prosthesis: d.prosthesis || indication, material: d.material || r.material || '',
            shade: d.shade || '', implant: d.implant || '', ...(d.implantSystem ? { implantSystem: d.implantSystem } : {}), isPontic: !!d.isPontic, isBridge: !!d.isBridge,
        }))
        : (r.teeth || []).map(t => ({
            toothNumber: t, prosthesis: indication, material: r.material || '',
            shade: '', implant: '', isPontic: false, isBridge: false,
        }));
};

export const caseToOrderShape = (r, { partnerName = '', labId = '', labName = '' } = {}) => {
    const fileDate = r.fileMtime ? new Date(r.fileMtime) : new Date();
    return {
        id: `case:${r.id}`,          // 실제 주문 문서가 아님을 표시 (2단계에서 명세서 items[].caseId로 기록)
        caseId: r.id,
        parsedCaseId: r.id,
        source: 'agent_auto',        // buildItemsFromOrders의 한글 표준명칭 변환 분기를 그대로 타야 함
        fromUserId: r.partnerId || null,
        fromUserName: partnerName || r.partnerName || r.clientNameInFile || '치과',
        toUserId: labId || r.labId || '',
        toUserName: labName,
        patientName: displayPatientName(r).name || '환자미상',
        patientNameRaw: r.patientNameRaw || r.patientName || '',
        items: caseItemsOf(r),
        prosthesisGroups: [], bridgeGroups: [], ponticGroups: [], implantGroups: [],
        createdAt: fileDate,
        dueDate: fileDate,
        designDate: fileDate,   // 디자인 완성일(10-31) = CAD 파일 날짜
        isRemake: !!r.isRemake,
        remakeReason: r.isRemake ? '에이전트 감지 (재제작 파일)' : '',
        specialNotes: `에이전트 자동 수집 (${r.sourceType} · ${r.sourceFile || ''})${r.isRemake ? ' · 리메이크' : ''}`,
        _case: r,
    };
};

// 수집 케이스가 이 거래처의 것인가 (CreateTransactionStatement.js:656-662 collectClinicMatch 동일)
export const caseMatchesClinic = (r, clinicSel, partnerName, opt = {}) => {
    if (r.partnerId && r.partnerId === clinicSel) return true;
    if (r.partnerId) return false;
    const pn = normClinicKey(partnerName, opt);
    const cn = normClinicKey(r.clientNameInFile, opt);
    return !!(pn && cn && (pn === cn || pn.includes(cn) || cn.includes(pn)));
};

// 기간(작업일 fileMtime) 필터 — 문자열 비교 (CreateTransactionStatement.js:942-944 동일)
export const caseInRange = (r, sd, ed) => {
    const d = (r.fileMtime || '').slice(0, 10);
    if (sd && d < sd) return false;
    if (ed && d > ed) return false;
    return true;
};

// ── 병합 (정본 1벌) ─────────────────────────────────────────────────────────
/**
 * 웹 의뢰서 + 수집 케이스를 하나의 케이스 목록으로 합친다.
 * @param {object} p
 *  - webOrders   : workOrders 중 source!=='agent_auto' (기간·거래처 필터는 호출자가 끝낸 상태)
 *  - cases       : parsedCases 문서 배열 (status excluded/scan_only 제외는 호출자 또는 옵션)
 *  - legacyAgentOrders : 기존 agent_auto 주문 (2·3단계 전 구데이터). cases에 대응 문서가 없는 것만 살린다
 *  - basis       : 'createdAt' | 'dueDate' — 정렬 기준
 *  - partnerName, labId, labName : 케이스→주문 형태 변환용 표기
 *  - nfcNormalize, remakeAgainstWeb : 위 상단 주석 참조 (기본값 = 현행 동작)
 * @returns {Array} rows [{ key, origin, order, webOrder, caseOrder, case, orderId, caseId, mergedCaseIds, patientName, teeth, materials, monthKey, isRemake }]
 *   - order = 화면·청구에 쓸 대표 문서(웹이 있으면 웹 우선 — 현행과 동일)
 */
export function mergeCases({
    webOrders = [], cases = [], legacyAgentOrders = [],
    basis = 'createdAt', partnerName = '', labId = '', labName = '',
    nfcNormalize = false, remakeAgainstWeb = false,
} = {}) {
    const opt = { nfcNormalize };
    const nm = (n) => normPatientKey(n, opt);

    // 0) 수집 풀 구성: parsedCases 우선. 대응 케이스가 없는 구 agent_auto 주문만 보존(고아 legacy)
    const caseIds = new Set(cases.map(c => c.id));
    const collected = [
        ...cases.map(c => caseToOrderShape(c, { partnerName, labId, labName })),
        ...legacyAgentOrders.filter(o => !(o.parsedCaseId && caseIds.has(o.parsedCaseId)))
            .map(o => ({ ...o, _legacyOrphan: true })),
    ];

    // 1) 빈 의뢰서 제외 (웹·수집 공통 — 현행 :1134)
    const web = webOrders.filter(hasContent);
    const coll = collected.filter(hasContent);

    // 2) 웹 우선 — 환자명 유사 + 치식 동일이면 웹 행에 흡수(origin: both)
    const webRows = web.map(o => ({
        key: `web:${o.id}`, origin: ORIGIN.WEB, order: o, webOrder: o,
        caseOrder: null, case: null, orderId: o.id, caseId: null,
        mergedCaseIds: [], patientName: o.patientName || '', teeth: teethKeyOf(o),
        materials: materialSetOf(o), monthKey: monthKeyOf(o), isRemake: !!o.isRemake,
    }));
    const restCollected = [];
    for (const co of coll) {
        const an = nm(co.patientName), at = teethKeyOf(co);
        // remakeAgainstWeb=true면 리메이크 수집분은 웹에 흡수되지 않는다 (기본 false = 현행 명세서 경로)
        const hit = (remakeAgainstWeb && co.isRemake)
            ? -1
            : webRows.findIndex(w => w.teeth === at && samePatientName(nm(w.patientName), an));
        if (hit >= 0) {
            const w = webRows[hit];
            w.origin = ORIGIN.BOTH;
            if (!w.caseOrder) { w.caseOrder = co; w.case = co._case || null; w.caseId = co.caseId || co.parsedCaseId || null; }
            // 디자인 완성일(10-31) — 웹 의뢰서에 짝이 맞는 CAD 파일 날짜(가장 이른 것)
            { const t = getTimestamp(co.designDate || co.createdAt), cur = getTimestamp(w.order.designDateFromCase); if (t && (!cur || t < cur)) { w.order = { ...w.order, designDate: new Date(t), designDateFromCase: new Date(t) }; } }
            if (co.caseId || co.parsedCaseId) w.mergedCaseIds.push(co.caseId || co.parsedCaseId);
        } else {
            restCollected.push(co);
        }
    }

    // 3) 수집분끼리 병합 — 환자·치식 + (같은 달 또는 작업일 7일 이내) + 재료 부분집합, 리메이크는 별건
    //    (현행 CreateTransactionStatement.js:1164-1192 agentDeduped2와 동일 규칙·동일 승자 선정)
    const collRows = [];
    for (const co of restCollected) {
        const an = nm(co.patientName), at = teethKeyOf(co);
        const am = monthKeyOf(co), as = materialSetOf(co);
        const closeInTime = (x) => {
            if (monthKeyOf(x) === am) return true;
            const tx = getTimestamp(x.createdAt), ta = getTimestamp(co.createdAt);
            return !!(tx && ta && Math.abs(tx - ta) <= 7 * 86400000);
        };
        const dupIdx = collRows.findIndex(r =>
            samePatientName(nm(r.order.patientName), an) && r.teeth === at &&
            closeInTime(r.order) && isMaterialSubset(r.materials, as) &&
            !r.order.isRemake && !co.isRemake);
        if (dupIdx >= 0) {
            const r = collRows[dupIdx];
            const prev = r.order;
            // 항목이 많은(정보가 풍부한) 쪽을 남기되, 작업일은 빠른 쪽 유지 (대표 확정 2026-08-05)
            const winner = (co.items || []).length > (prev.items || []).length ? co : prev;
            const tPrev = getTimestamp(prev.createdAt), tCo = getTimestamp(co.createdAt);
            const earlier = Math.min(tPrev || Infinity, tCo || Infinity);
            const merged = (isFinite(earlier) && earlier !== getTimestamp(winner.createdAt))
                ? { ...winner, createdAt: new Date(earlier), designDate: new Date(earlier) }
                : winner;
            r.order = merged; r.caseOrder = merged; r.case = merged._case || r.case;
            r.materials = materialSetOf(merged);
            r.monthKey = monthKeyOf(merged);
            const cid = co.caseId || co.parsedCaseId;
            if (cid && !r.mergedCaseIds.includes(cid)) r.mergedCaseIds.push(cid);
        } else {
            collRows.push({
                key: `case:${co.caseId || co.parsedCaseId || co.id}`,
                origin: ORIGIN.COLLECTED, order: co, webOrder: null, caseOrder: co, case: co._case || null,
                orderId: co._legacyOrphan ? co.id : null, caseId: co.caseId || co.parsedCaseId || null,
                mergedCaseIds: [co.caseId || co.parsedCaseId].filter(Boolean),
                patientName: co.patientName || '', teeth: teethKeyOf(co),
                materials: materialSetOf(co), monthKey: monthKeyOf(co), isRemake: !!co.isRemake,
                legacyOrphan: !!co._legacyOrphan,
            });
        }
    }

    // 4) 웹 먼저, 그 다음 수집분 → 날짜 오름차순 (현행 :1194-1201과 동일. sort는 안정 정렬이라 동률 시 웹 우선 유지)
    const rows = [...webRows, ...collRows];
    rows.sort((a, b) => {
        const ka = basis === 'dueDate' ? a.order.dueDate : (a.order.designDate || a.order.createdAt);   // 10-31 디자인 완성일순
        const kb = basis === 'dueDate' ? b.order.dueDate : (b.order.designDate || b.order.createdAt);
        return getTimestamp(ka) - getTimestamp(kb);
    });
    return rows;
}

/** 병합 결과 → 항목 생성기(buildItemsFromOrders)에 넣을 주문 배열 */
export const ordersOf = (rows) => rows.map(r => r.order);

// ── 섀도 비교 ───────────────────────────────────────────────────────────────
// 명세서 항목(buildItemsFromOrders 결과)을 "사업 의미" 기준으로 비교한다.
// 문서 id(orderId)는 신규 경로에서 case: 접두라 다를 수밖에 없으므로 키에서 제외.
// 환자명 마스킹 — 로그·콘솔로 나가는 문자열 전용 (회사 규정: 로그에 환자 식별정보 금지)
// 비교 키 자체는 원문을 쓴다(마스킹하면 서로 다른 환자가 같은 키로 뭉쳐 diff가 가려진다).
// 예: 홍길동 → 홍○동, 김민 → 김○, Smith → S***h
export const maskPatientForLog = (n) => {
    const s = String(n == null ? '' : n).trim();
    if (!s) return '(무명)';
    if (s.length === 1) return '○';
    if (s.length === 2) return s[0] + '○';
    return s[0] + '○'.repeat(s.length - 2) + s[s.length - 1];
};
// 비교 키(첫 칸이 환자명)에서 환자명만 가린 표시용 문자열
const maskKeyForLog = (k) => {
    const parts = String(k == null ? '' : k).split('|');
    parts[0] = maskPatientForLog(parts[0]);
    return parts.join('|');
};

const itemKey = (it) => [
    (it.patientName || '').trim(),
    (it.toothInfo || '').trim(),
    (it.serviceName || '').trim(),
    (it.material || '').trim(),
    it.toothCount || 0,
].join('|');
const itemAmount = (it) => (Number(it.toothCount) || 0) * (Number(it.unitPrice) || 0);

/** 두 항목 배열의 차이 — 건수·금액·누락/추가 목록 */
export function diffItems(currentItems = [], nextItems = []) {
    const bag = (arr) => {
        const m = new Map();
        arr.forEach(it => {
            const k = itemKey(it);
            const cur = m.get(k) || { key: k, count: 0, amount: 0, sample: it };
            cur.count += 1; cur.amount += itemAmount(it);
            m.set(k, cur);
        });
        return m;
    };
    const A = bag(currentItems), B = bag(nextItems);
    const missing = [], added = [], changed = [];
    for (const [k, a] of A) {
        const b = B.get(k);
        if (!b) { missing.push(a); continue; }
        if (a.count !== b.count || Math.round(a.amount) !== Math.round(b.amount)) {
            changed.push({ key: k, current: { count: a.count, amount: a.amount }, next: { count: b.count, amount: b.amount } });
        }
    }
    for (const [k, b] of B) if (!A.has(k)) added.push(b);
    const sum = (arr) => arr.reduce((s, it) => s + itemAmount(it), 0);
    const amountCurrent = sum(currentItems), amountNext = sum(nextItems);
    return {
        ok: missing.length === 0 && added.length === 0 && changed.length === 0 && Math.round(amountCurrent) === Math.round(amountNext),
        countCurrent: currentItems.length, countNext: nextItems.length,
        amountCurrent, amountNext, amountDiff: Math.round(amountNext - amountCurrent),
        missing,   // 현행에는 있고 신규에 없는 것 = 청구 누락 위험 (차단 사유)
        added,     // 신규에만 있는 것 = 과거 누락분일 수 있음
        changed,
    };
}

/** 섀도 로그 — 화면에는 아무 것도 바꾸지 않고 콘솔에만 남긴다. 절대 throw하지 않는다. */
export function logShadowDiff(label, diff, extra = {}) {
    try {
        const tag = `[케이스통일 섀도] ${label}`;
        if (diff.ok) {
            console.log(`${tag} 일치 — 항목 ${diff.countCurrent}건 / ${Math.round(diff.amountCurrent).toLocaleString('ko-KR')}원`, extra);
            return;
        }
        console.groupCollapsed(`${tag} 차이 발견 — 건수 ${diff.countCurrent}→${diff.countNext}, 금액 ${diff.amountDiff >= 0 ? '+' : ''}${diff.amountDiff.toLocaleString('ko-KR')}원`);
        console.log('요약', { ...extra, countCurrent: diff.countCurrent, countNext: diff.countNext, amountCurrent: diff.amountCurrent, amountNext: diff.amountNext });
        // 환자명은 마스킹해서 남긴다 (콘솔·원격 로그 수집에 원문이 흐르지 않게 — 회사 규정 '환자 정보')
        if (diff.missing.length) console.warn('현행에만 있음(누락 위험)', diff.missing.map(x => maskKeyForLog(x.key)));
        if (diff.added.length) console.warn('신규에만 있음(추가)', diff.added.map(x => maskKeyForLog(x.key)));
        if (diff.changed.length) console.warn('건수·금액 차이', diff.changed.map(c => ({ ...c, key: maskKeyForLog(c.key) })));
        console.groupEnd();
    } catch (_) { /* 로깅 실패가 화면을 막지 않게 — 단, 무시는 이 한 줄뿐 */ }
}

// ※ Firestore 조회 도우미는 src/utils/caseViewLoad.js 에 따로 둔다.
//    이 파일은 firebase 의존이 없는 **순수 모듈**로 유지한다 —
//    (a) Node 비교 스크립트(scratchpad/case-unify-sim.js)가 그대로 import할 수 있고,
//    (b) 여기에 동적 import를 두면 webpack 모듈 결합이 깨져 main 번들이 ~35kB(gzip) 커진다(실측).
